/**
 * classesService.ts
 *
 * Single source of truth for reading teacher classes from Firestore.
 *
 * Data model (written by importGoogleClassroom Cloud Function):
 *   users/{teacherId}/classes/{courseId}          — class summary doc
 *   users/{teacherId}/classes/{courseId}/students/{studentUid} — membership subcollection
 *
 * Legacy data model (written by ClassManager manual class creation):
 *   users/{teacherId}.classes[]                   — array field on teacher doc
 *
 * listTeacherClasses() merges both sources so teachers see all their classes
 * regardless of how they were created.
 */
import { collection, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';

export interface TeacherClass {
  id: string;
  name: string;
  studentCount: number;
  googleClassroomId?: string;
  color?: string;
  /** Loaded lazily via listClassStudentIds() */
  studentIds?: string[];
  /** Data source: google_classroom (subcollection) or manual (array field) */
  source: 'google_classroom' | 'manual';
  /** If true, UI should disable edit/delete actions */
  readOnly: boolean;
}

/**
 * Lists all classes for a teacher, merging:
 *  1) Subcollection: users/{teacherId}/classes/{courseId}  (Google Classroom imports)
 *  2) Array field: users/{teacherId}.classes[]              (manually created classes)
 *
 * Does NOT fetch student membership (lazy — use listClassStudentIds() on demand).
 */
export async function listTeacherClasses(teacherId: string): Promise<TeacherClass[]> {
  const classMap = new Map<string, TeacherClass>();

  // Source 1: subcollection (Google Classroom imports via Cloud Function)
  try {
    const snap = await getDocs(collection(db, 'users', teacherId, 'classes'));
    for (const d of snap.docs) {
      const data = d.data();
      classMap.set(d.id, {
        id: d.id,
        name: data.name || data.courseName || 'Unnamed Class',
        studentCount: data.studentCount ?? 0,
        googleClassroomId: data.googleClassroomId,
        color: data.color,
        source: 'google_classroom',
        readOnly: true,
      });
    }
  } catch (err) {
    console.error('[classesService] Failed to read classes subcollection:', err);
  }

  // Source 2: array field on teacher doc (manually created classes in ClassManager)
  try {
    const teacherSnap = await getDoc(doc(db, 'users', teacherId));
    if (teacherSnap.exists()) {
      const manualClasses: any[] = teacherSnap.data()?.classes || [];
      for (const cls of manualClasses) {
        if (!cls?.id) continue;
        // Don't overwrite a subcollection entry with the same ID
        if (!classMap.has(cls.id)) {
          classMap.set(cls.id, {
            id: cls.id,
            name: cls.name || cls.className || 'Unnamed Class',
            studentCount: cls.studentIds?.length ?? cls.students?.length ?? 0,
            color: cls.color,
            // Manual classes already have student IDs inline
            studentIds: cls.studentIds || [],
            source: 'manual',
            readOnly: false,
          });
        }
      }
    }
  } catch (err) {
    console.error('[classesService] Failed to read manual classes from teacher doc:', err);
  }

  return Array.from(classMap.values());
}

/**
 * Fetches student UIDs for a specific class.
 *
 * For Google Classroom imports: reads membership subcollection
 *   users/{teacherId}/classes/{courseId}/students/{studentUid}
 * For manual classes: studentIds are already in the TeacherClass object (passed as fallback).
 *
 * @param inlineStudentIds - already-known student IDs (from manual class); skips subcollection read if provided.
 */
export async function listClassStudentIds(
  teacherId: string,
  courseId: string,
  inlineStudentIds?: string[]
): Promise<string[]> {
  // If manual class already has inline student IDs, return them directly
  if (inlineStudentIds && inlineStudentIds.length > 0) {
    return inlineStudentIds;
  }

  try {
    const snap = await getDocs(
      collection(db, 'users', teacherId, 'classes', courseId, 'students')
    );
    return snap.docs.map(d => d.data().studentId || d.id);
  } catch (err) {
    console.error(`[classesService] Failed to read members for class ${courseId}:`, err);
    return [];
  }
}
